import { Shield, FileText, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

export default function TermsOfService() {
  const lastUpdated = "February 12, 2025";

  const sections = [
    {
      id: "acceptance",
      title: "1. Acceptance of Terms",
      content: `By accessing or using PrivacyShield's website, applications, and services (collectively, the "Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Service.

These Terms constitute a legally binding agreement between you and PrivacyShield ("we," "us," or "our"). We may update these Terms from time to time, and your continued use of the Service constitutes acceptance of any changes.

You must be at least 16 years old to use our Service. By using the Service, you represent and warrant that you meet this age requirement.`
    },
    {
      id: "description",
      title: "2. Description of Service",
      content: `PrivacyShield provides digital privacy protection services, including but not limited to:

• **Password Breach Checking:** Verification of passwords against known data breach databases
• **Email Breach Monitoring:** Scanning for email addresses in known data breaches
• **Data Broker Removal:** Assistance with removing personal information from data broker databases
• **Privacy Education:** Guides and resources for improving digital privacy
• **AI Privacy Assistant:** Automated assistance for privacy-related questions

**Service Limitations:**
Our breach checking uses the Have I Been Pwned database and other sources. While we strive for accuracy, we cannot guarantee detection of all breaches. Data broker removal services initiate removal requests but cannot guarantee removal from all databases, as compliance depends on third-party data brokers.`
    },
    {
      id: "accounts",
      title: "3. User Accounts",
      content: `**Account Creation:**
To access certain features, you must create an account. You agree to:
• Provide accurate, current, and complete information
• Maintain the security of your password
• Accept responsibility for all activities under your account
• Notify us immediately of any unauthorized access

**Account Termination:**
We reserve the right to suspend or terminate accounts that:
• Violate these Terms
• Engage in fraudulent or illegal activity
• Abuse the Service or other users
• Remain inactive for extended periods (with prior notice)

You may close your account at any time through your account settings or by contacting support.`
    },
    {
      id: "subscriptions",
      title: "4. Subscriptions & Payments",
      content: `**Free Tier:**
Basic features are available at no cost, subject to usage limits.

**Premium Subscriptions:**
• Subscriptions are billed monthly or annually as selected at purchase
• Payment is processed securely through Stripe
• Prices are in USD unless otherwise specified
• Subscriptions auto-renew unless cancelled before the renewal date

**Cancellation & Refunds:**
• You may cancel your subscription at any time
• Cancellation takes effect at the end of the current billing period
• We offer a 14-day money-back guarantee for new premium subscriptions
• Refund requests after 14 days are considered on a case-by-case basis
• To request a refund, contact support@privacyshield.store

**Price Changes:**
We may change subscription prices with 30 days' notice. Price changes will take effect at your next renewal date.`
    },
    {
      id: "acceptable-use",
      title: "5. Acceptable Use Policy",
      content: `You agree NOT to use the Service to:

**Prohibited Activities:**
• Violate any applicable laws or regulations
• Infringe on intellectual property rights of others
• Transmit malware, viruses, or harmful code
• Attempt to gain unauthorized access to our systems
• Interfere with or disrupt the Service
• Scrape, crawl, or collect data without permission
• Use the Service for competitive analysis
• Resell or redistribute the Service without authorization
• Harass, abuse, or harm other users
• Submit false or misleading information
• Use automated tools to access the Service (except as permitted)

**Password Checking:**
The password breach checker is for personal security verification only. You may not use it to:
• Check passwords belonging to others without consent
• Conduct bulk password testing
• Attempt to crack or reverse-engineer passwords

Violation of this policy may result in immediate account termination.`
    },
    {
      id: "intellectual-property",
      title: "6. Intellectual Property",
      content: `**Our Content:**
The Service, including its design, text, graphics, logos, and software, is owned by PrivacyShield and protected by copyright, trademark, and other intellectual property laws.

**Your License:**
We grant you a limited, non-exclusive, non-transferable license to access and use the Service for personal, non-commercial purposes in accordance with these Terms.

**Restrictions:**
You may not:
• Copy, modify, or distribute our content
• Reverse engineer or decompile our software
• Remove any copyright or proprietary notices
• Use our trademarks without written permission

**User Content:**
You retain ownership of content you submit (e.g., support messages). By submitting content, you grant us a license to use it for providing and improving the Service.`
    },
    {
      id: "third-party",
      title: "7. Third-Party Services",
      content: `Our Service integrates with third-party services:

**Have I Been Pwned:** Password and breach data provided by haveibeenpwned.com
**Stripe:** Payment processing
**AI Services:** Powered by OpenAI technology

We are not responsible for the availability, accuracy, or practices of third-party services. Your use of third-party services is subject to their respective terms and policies.

**Affiliate Links:**
Our toolkit may contain affiliate links. We may earn commissions from purchases made through these links at no extra cost to you. We only recommend products we believe provide value.`
    },
    {
      id: "disclaimers",
      title: "8. Disclaimers",
      content: `**THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED.**

We specifically disclaim:
• Warranties of merchantability or fitness for a particular purpose
• Warranties that the Service will be uninterrupted or error-free
• Warranties regarding the accuracy or completeness of breach data
• Warranties that data broker removal requests will be honored

**No Security Guarantee:**
While we implement robust security measures, no system is completely secure. We cannot guarantee that your information will never be accessed, disclosed, or destroyed.

**Not Legal Advice:**
Our Service provides general privacy information and tools. It does not constitute legal advice. Consult a qualified attorney for legal guidance.`
    },
    {
      id: "limitation",
      title: "9. Limitation of Liability",
      content: `**TO THE MAXIMUM EXTENT PERMITTED BY LAW:**

PrivacyShield and its officers, directors, employees, and agents shall not be liable for:
• Any indirect, incidental, special, consequential, or punitive damages
• Loss of profits, data, use, goodwill, or other intangible losses
• Damages resulting from unauthorized access to your account
• Damages resulting from third-party actions or inactions
• Damages exceeding the amount paid by you in the 12 months preceding the claim

Some jurisdictions do not allow limitation of liability for certain damages. In such cases, our liability is limited to the maximum extent permitted by law.`
    },
    {
      id: "indemnification",
      title: "10. Indemnification",
      content: `You agree to indemnify, defend, and hold harmless PrivacyShield and its officers, directors, employees, agents, and affiliates from any claims, damages, losses, liabilities, costs, and expenses (including reasonable attorneys' fees) arising from:

• Your use of the Service
• Your violation of these Terms
• Your violation of any third-party rights
• Your content or data submitted to the Service
• Any misrepresentation made by you`
    },
    {
      id: "governing-law",
      title: "11. Governing Law & Disputes",
      content: `**Governing Law:**
These Terms are governed by the laws of the State of Delaware, United States, without regard to conflict of law principles.

**Dispute Resolution:**
Before filing a claim, you agree to attempt to resolve disputes informally by contacting us at legal@privacyshield.store. We will attempt to resolve the dispute within 30 days.

**Arbitration Agreement:**
Any disputes not resolved informally shall be resolved through binding arbitration in accordance with the American Arbitration Association rules. You waive any right to participate in class actions.

**Exceptions:**
Either party may seek injunctive relief in court for intellectual property violations or unauthorized access.`
    },
    {
      id: "general",
      title: "12. General Provisions",
      content: `**Entire Agreement:**
These Terms, along with our Privacy Policy, constitute the entire agreement between you and PrivacyShield.

**Severability:**
If any provision is found unenforceable, the remaining provisions remain in effect.

**Waiver:**
Our failure to enforce any right does not waive our right to enforce it later.

**Assignment:**
You may not assign these Terms without our consent. We may assign our rights and obligations without restriction.

**Force Majeure:**
We are not liable for failures due to circumstances beyond our reasonable control.

**Notices:**
We may send notices via email or through the Service. You may contact us at legal@privacyshield.store.`
    },
    {
      id: "contact",
      title: "13. Contact Information",
      content: `For questions about these Terms, please contact us:

**Email:** legal@privacyshield.store
**Support:** support@privacyshield.store
**AI Assistant:** Chat with Alek at privacyshield.store/contact

**Mailing Address:**
PrivacyShield
[Your Business Address]`
    }
  ];

  return (
    <div className="min-h-screen bg-[#050505] py-8 md:py-16" data-testid="terms-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#00FF94]/10 border border-[#00FF94]/20 mb-4">
            <FileText className="w-8 h-8 text-[#00FF94]" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            Terms of Service
          </h1>
          <p className="text-gray-400">
            Last updated: {lastUpdated}
          </p>
        </div>

        {/* Key Points */}
        <Card className="bg-[#0a0a0a] border-[#262626] mb-8">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Key Points</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#00FF94] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-white font-medium">14-Day Money-Back</p>
                  <p className="text-xs text-gray-500">Full refund within 14 days of purchase</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#00FF94] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-white font-medium">Cancel Anytime</p>
                  <p className="text-xs text-gray-500">No lock-in contracts or hidden fees</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#00FF94] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-white font-medium">Your Data, Your Control</p>
                  <p className="text-xs text-gray-500">Delete your account and data anytime</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-white font-medium">Service Limitations</p>
                  <p className="text-xs text-gray-500">Results depend on third-party data sources</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table of Contents */}
        <Card className="bg-[#0a0a0a] border-[#262626] mb-8">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Table of Contents</h2>
            <nav className="grid md:grid-cols-2 gap-2">
              {sections.map((section) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className="text-sm text-gray-400 hover:text-[#00FF94] transition-colors"
                >
                  {section.title}
                </a>
              ))}
            </nav>
          </CardContent>
        </Card>

        {/* Terms Sections */}
        <div className="space-y-8">
          {sections.map((section) => (
            <section key={section.id} id={section.id} className="scroll-mt-8">
              <Card className="bg-[#0a0a0a] border-[#262626]">
                <CardContent className="p-6">
                  <h2 className="text-xl font-semibold text-white mb-4">{section.title}</h2>
                  <div className="text-gray-400 text-sm leading-relaxed whitespace-pre-wrap">
                    {section.content.split('**').map((part, i) => 
                      i % 2 === 1 ? <strong key={i} className="text-white">{part}</strong> : part
                    )}
                  </div>
                </CardContent>
              </Card>
            </section>
          ))}
        </div>

        {/* Acceptance Notice */}
        <Card className="bg-[#00FF94]/5 border-[#00FF94]/20 mt-8">
          <CardContent className="p-6 text-center">
            <p className="text-sm text-gray-300">
              By using PrivacyShield, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
            </p>
            <p className="text-xs text-gray-500 mt-2">
              Questions? <a href="/contact" className="text-[#00FF94] hover:underline">Contact Alek</a> or email legal@privacyshield.store
            </p>
          </CardContent>
        </Card>

      </div>
    </div>
  );
}
